import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAnalytics } from "@/lib/analytics";
import {
  transcribeVoice,
  VoiceTranscriptionError,
} from "@/lib/api/voice";
import { voiceFeatureFlags } from "@/lib/voice/feature-flags";

type RecognitionErrorCode =
  | "aborted"
  | "audio-capture"
  | "network"
  | "no-speech"
  | "not-allowed"
  | "service-not-allowed"
  | "unknown";

interface SpeechRecognitionResultLike {
  readonly isFinal: boolean;
  readonly 0: {
    readonly transcript: string;
  };
}

interface SpeechRecognitionEventLike extends Event {
  readonly results: ArrayLike<SpeechRecognitionResultLike>;
}

interface SpeechRecognitionErrorEventLike extends Event {
  readonly error: RecognitionErrorCode;
}

interface SpeechRecognitionLike extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

const ERROR_COPY = "Transcription failed. Retry or type manually?";
const MAX_RETRIES = 2;
const DEFAULT_TRANSCRIPTION_LOCALE = "en-US";

export type VoiceInputState =
  | { state: "idle" }
  | { state: "listening" }
  | { state: "processing" }
  | { state: "error"; canRetry: boolean; errorMessage: string };

interface UseVoiceInputParams {
  enabled: boolean;
  mode: "preview" | "runtime";
  disabled?: boolean;
  questionRef: string;
  questionType: "short_text" | "long_text";
  currentValue: string;
  locale?: string;
  sessionToken?: string;
  projectId?: string;
  formId?: string;
  version?: number;
  ensureSessionToken?: () => Promise<string | null>;
  onTranscriptMerged: (nextValue: string) => void;
}

const getSpeechRecognitionConstructor = (): SpeechRecognitionConstructor | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const candidate = (
    window as unknown as {
      SpeechRecognition?: SpeechRecognitionConstructor;
      webkitSpeechRecognition?: SpeechRecognitionConstructor;
    }
  ).SpeechRecognition
    ?? (
      window as unknown as {
        webkitSpeechRecognition?: SpeechRecognitionConstructor;
      }
    ).webkitSpeechRecognition;

  return candidate ?? null;
};

const mergeTranscript = (current: string, transcript: string): string => {
  const cleanTranscript = transcript.trim();
  if (!cleanTranscript) {
    return current;
  }

  if (!current.trim()) {
    return cleanTranscript;
  }

  const currentTrimmed = current.trimEnd();
  if (
    currentTrimmed.toLocaleLowerCase().endsWith(
      cleanTranscript.toLocaleLowerCase(),
    )
  ) {
    return currentTrimmed;
  }

  const separator = /\s$/.test(current) ? "" : " ";
  return `${current}${separator}${cleanTranscript}`;
};

const getBestRecorderMimeType = (): string | undefined => {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) {
    return undefined;
  }

  const preferredTypes = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/mpeg",
    "audio/wav",
  ];

  return preferredTypes.find((type) => MediaRecorder.isTypeSupported(type));
};

const normalizeRecorderMimeType = (mimeType: string): string => {
  if (mimeType.includes("webm")) {
    return "audio/webm";
  }
  if (mimeType.includes("mp4")) {
    return "audio/mp4";
  }
  if (mimeType.includes("mpeg") || mimeType.includes("mp3")) {
    return "audio/mpeg";
  }
  if (mimeType.includes("wav")) {
    return "audio/wav";
  }
  return "audio/webm";
};

export function useVoiceInput({
  enabled,
  mode,
  disabled,
  questionRef,
  questionType,
  currentValue,
  locale,
  sessionToken,
  projectId,
  formId,
  version,
  ensureSessionToken,
  onTranscriptMerged,
}: UseVoiceInputParams) {
  const { track } = useAnalytics();
  const [voiceState, setVoiceState] = useState<VoiceInputState>({
    state: "idle",
  });
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [permissionMessage, setPermissionMessage] = useState<string | null>(
    null,
  );

  const recognitionCtor = useMemo(getSpeechRecognitionConstructor, []);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const recordingStartedAtRef = useRef<number>(0);
  const audioChunksRef = useRef<BlobPart[]>([]);
  const browserTranscriptRef = useRef("");
  const currentValueRef = useRef(currentValue);
  const lastBlobRef = useRef<Blob | null>(null);
  const lastDurationRef = useRef<number | undefined>(undefined);
  const lastAttemptRef = useRef(1);

  useEffect(() => {
    currentValueRef.current = currentValue;
  }, [currentValue]);

  const voiceAvailable = useMemo(() => {
    return (
      enabled &&
      mode === "runtime" &&
      !disabled &&
      voiceFeatureFlags.voiceInputEnabled
    );
  }, [disabled, enabled, mode]);

  const stopMediaResources = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (recognitionRef.current) {
      recognitionRef.current.onresult = null;
      recognitionRef.current.onerror = null;
      recognitionRef.current.onend = null;
      try {
        recognitionRef.current.stop();
      } catch {
        // No-op
      }
      recognitionRef.current = null;
    }

    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try {
        recorderRef.current.stop();
      } catch {
        // No-op
      }
    }
    recorderRef.current = null;

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      stopMediaResources();
    };
  }, [stopMediaResources]);

  useEffect(() => {
    if (!voiceAvailable || typeof navigator === "undefined") {
      return;
    }

    const permissionsApi = (
      navigator as Navigator & {
        permissions?: {
          query: (permission: { name: string }) => Promise<PermissionStatus>;
        };
      }
    ).permissions;

    if (!permissionsApi?.query) {
      return;
    }

    let mounted = true;
    let status: PermissionStatus | null = null;

    void permissionsApi
      .query({ name: "microphone" })
      .then((permissionStatus) => {
        if (!mounted) {
          return;
        }
        status = permissionStatus;

        const sync = () => {
          const denied = permissionStatus.state === "denied";
          setPermissionDenied(denied);
          setPermissionMessage(
            denied
              ? "Microphone access is blocked. Type your answer instead."
              : null,
          );
        };

        sync();
        permissionStatus.onchange = sync;
      })
      .catch(() => {
        // Ignore permission API failures, runtime request will handle it.
      });

    return () => {
      mounted = false;
      if (status) {
        status.onchange = null;
      }
    };
  }, [voiceAvailable]);

  const trackVoiceEvent = useCallback(
    (
      eventName:
        | "voice_input_started"
        | "voice_input_completed"
        | "voice_input_failed"
        | "voice_input_retry_clicked"
        | "voice_permission_denied",
      properties: {
        engine: "browser" | "server";
        provider?: "openai" | "google";
        attempt?: number;
        durationMs?: number;
      },
    ) => {
      track(eventName, {
        projectId,
        formId,
        version,
        questionRef,
        questionType,
        engine: properties.engine,
        provider: properties.provider,
        locale: locale ?? navigator.language ?? "en-US",
        attempt: properties.attempt ?? 1,
        durationMs: properties.durationMs,
      });
    },
    [formId, locale, projectId, questionRef, questionType, track, version],
  );

  const ensureToken = useCallback(async (): Promise<string | null> => {
    if (sessionToken) {
      return sessionToken;
    }
    if (ensureSessionToken) {
      return ensureSessionToken();
    }
    return null;
  }, [ensureSessionToken, sessionToken]);

  const handleTranscriptionFailure = useCallback(
    (attempt: number) => {
      const canRetry = attempt <= MAX_RETRIES && Boolean(lastBlobRef.current);
      setVoiceState({
        state: "error",
        canRetry,
        errorMessage: ERROR_COPY,
      });
    },
    [],
  );

  const transcribeWithServer = useCallback(
    async (audioBlob: Blob, attempt: number, durationMs?: number) => {
      if (
        !voiceFeatureFlags.serverFallbackEnabled ||
        !projectId ||
        !formId ||
        !version
      ) {
        handleTranscriptionFailure(attempt);
        return;
      }

      setVoiceState({ state: "processing" });
      lastAttemptRef.current = attempt;
      lastBlobRef.current = audioBlob;
      lastDurationRef.current = durationMs;

      try {
        const resolvedToken = await ensureToken();
        if (!resolvedToken) {
          throw new VoiceTranscriptionError(
            "Session could not be initialized for voice transcription.",
            "SESSION_REQUIRED",
          );
        }

        const requestedLocale =
          locale ?? navigator.language ?? DEFAULT_TRANSCRIPTION_LOCALE;

        const response = await transcribeVoice({
          projectId,
          formId,
          version,
          sessionToken: resolvedToken,
          audio: audioBlob,
          locale: requestedLocale,
          questionRef,
          attempt,
          durationMs,
        });

        // Backward-compatible UX guard:
        // if an older API still rejects locale, retry with stable default.
        // New API behavior should already normalize unsupported locales server-side.
        if (response.transcript.trim().length === 0) {
          throw new VoiceTranscriptionError(
            "Transcription failed. Retry or type manually?",
            "VOICE_TRANSCRIPTION_EMPTY",
          );
        }

        const mergedValue = mergeTranscript(
          currentValueRef.current,
          response.transcript,
        );
        onTranscriptMerged(mergedValue);
        setVoiceState({ state: "idle" });
        lastBlobRef.current = null;
        trackVoiceEvent("voice_input_completed", {
          engine: "server",
          provider: response.provider,
          attempt,
          durationMs: response.durationMs,
        });
      } catch (error) {
        const code =
          error instanceof VoiceTranscriptionError ? error.code : undefined;
        if (
          code === "VOICE_LOCALE_UNSUPPORTED" &&
          (locale ?? navigator.language ?? DEFAULT_TRANSCRIPTION_LOCALE) !==
            DEFAULT_TRANSCRIPTION_LOCALE
        ) {
          try {
            const resolvedToken = await ensureToken();
            if (!resolvedToken) {
              throw new VoiceTranscriptionError(
                "Session could not be initialized for voice transcription.",
                "SESSION_REQUIRED",
              );
            }

            const fallbackResponse = await transcribeVoice({
              projectId: projectId!,
              formId: formId!,
              version: version!,
              sessionToken: resolvedToken,
              audio: audioBlob,
              locale: DEFAULT_TRANSCRIPTION_LOCALE,
              questionRef,
              attempt,
              durationMs,
            });

            const mergedValue = mergeTranscript(
              currentValueRef.current,
              fallbackResponse.transcript,
            );
            onTranscriptMerged(mergedValue);
            setVoiceState({ state: "idle" });
            lastBlobRef.current = null;
            trackVoiceEvent("voice_input_completed", {
              engine: "server",
              provider: fallbackResponse.provider,
              attempt,
              durationMs: fallbackResponse.durationMs,
            });
            return;
          } catch {
            handleTranscriptionFailure(attempt);
          }
        } else {
          handleTranscriptionFailure(attempt);
        }

        trackVoiceEvent("voice_input_failed", {
          engine: "server",
          attempt,
          durationMs,
        });
      }
    },
    [
      ensureToken,
      formId,
      handleTranscriptionFailure,
      locale,
      onTranscriptMerged,
      projectId,
      questionRef,
      trackVoiceEvent,
      version,
    ],
  );

  const stopListening = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      return;
    }

    setVoiceState({ state: "processing" });
    recorder.stop();
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        // No-op
      }
    }
  }, []);

  const startListening = useCallback(async () => {
    if (!voiceAvailable) {
      return;
    }

    if (voiceState.state === "listening" || voiceState.state === "processing") {
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setVoiceState({
        state: "error",
        canRetry: false,
        errorMessage: "Voice input is not available in this browser.",
      });
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setPermissionDenied(false);
      setPermissionMessage(null);

      const recorderMimeType = getBestRecorderMimeType();
      const recorder = recorderMimeType
        ? new MediaRecorder(stream, { mimeType: recorderMimeType })
        : new MediaRecorder(stream);
      recorderRef.current = recorder;

      audioChunksRef.current = [];
      browserTranscriptRef.current = "";

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const recordedDuration = recordingStartedAtRef.current
          ? Date.now() - recordingStartedAtRef.current
          : undefined;
        const recordedBlob = new Blob(audioChunksRef.current, {
          type: normalizeRecorderMimeType(recorder.mimeType || "audio/webm"),
        });

        stopMediaResources();

        const browserTranscript = browserTranscriptRef.current.trim();
        if (browserTranscript.length > 0) {
          const mergedValue = mergeTranscript(
            currentValueRef.current,
            browserTranscript,
          );
          onTranscriptMerged(mergedValue);
          setVoiceState({ state: "idle" });
          lastBlobRef.current = null;
          trackVoiceEvent("voice_input_completed", {
            engine: "browser",
            attempt: 1,
            durationMs: recordedDuration,
          });
          return;
        }

        if (
          recordedBlob.size === 0 ||
          !voiceFeatureFlags.serverFallbackEnabled
        ) {
          setVoiceState({
            state: "error",
            canRetry: false,
            errorMessage: ERROR_COPY,
          });
          trackVoiceEvent("voice_input_failed", {
            engine: "browser",
            attempt: 1,
            durationMs: recordedDuration,
          });
          return;
        }

        void transcribeWithServer(recordedBlob, 1, recordedDuration);
      };

      const maxDuration = Math.max(
        5_000,
        voiceFeatureFlags.maxRecordingDurationMs,
      );
      timeoutRef.current = window.setTimeout(() => {
        stopListening();
      }, maxDuration);

      stream.getTracks().forEach((track) => {
        track.onended = () => {
          stopListening();
          setVoiceState({
            state: "error",
            canRetry: false,
            errorMessage:
              "Microphone access ended. Retry or type your answer manually.",
          });
        };
      });

      recordingStartedAtRef.current = Date.now();

      if (recognitionCtor) {
        const recognition = new recognitionCtor();
        recognition.lang = locale ?? navigator.language ?? "en-US";
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.onresult = (event) => {
          const transcript =
            event.results?.[0]?.[0]?.transcript?.trim() ?? "";
          if (transcript.length > 0) {
            browserTranscriptRef.current = transcript;
          }
        };
        recognition.onerror = (event) => {
          if (
            event.error === "not-allowed" ||
            event.error === "service-not-allowed"
          ) {
            setPermissionDenied(true);
            setPermissionMessage(
              "Microphone access is blocked. Type your answer instead.",
            );
            trackVoiceEvent("voice_permission_denied", {
              engine: "browser",
              attempt: 1,
            });
          }
        };
        recognition.onend = () => {
          // Recorder stop path drives completion.
        };
        recognitionRef.current = recognition;
        try {
          recognition.start();
        } catch {
          recognitionRef.current = null;
        }
      }

      recorder.start(250);
      setVoiceState({ state: "listening" });
      trackVoiceEvent("voice_input_started", {
        engine: recognitionCtor ? "browser" : "server",
        attempt: 1,
      });
    } catch {
      setPermissionDenied(true);
      setPermissionMessage(
        "Microphone access is blocked. Type your answer instead.",
      );
      setVoiceState({ state: "idle" });
      trackVoiceEvent("voice_permission_denied", {
        engine: "browser",
        attempt: 1,
      });
    }
  }, [
    locale,
    onTranscriptMerged,
    recognitionCtor,
    stopListening,
    stopMediaResources,
    trackVoiceEvent,
    transcribeWithServer,
    voiceAvailable,
    voiceState.state,
  ]);

  const retryTranscription = useCallback(async () => {
    if (!lastBlobRef.current) {
      return;
    }

    const nextAttempt = lastAttemptRef.current + 1;
    trackVoiceEvent("voice_input_retry_clicked", {
      engine: "server",
      attempt: nextAttempt,
      durationMs: lastDurationRef.current,
    });
    await transcribeWithServer(
      lastBlobRef.current,
      nextAttempt,
      lastDurationRef.current,
    );
  }, [trackVoiceEvent, transcribeWithServer]);

  const micVisible = voiceAvailable && !permissionDenied;

  return {
    voiceState,
    micVisible,
    permissionDenied,
    permissionMessage,
    startListening,
    stopListening,
    retryTranscription,
  };
}
